import { supabase } from '../lib/supabase'
import type { AttendanceRecord, LeaveBalance } from '../types/index'

function rowToAttendance(r: Record<string, unknown>): AttendanceRecord {
  return {
    id:          r.id as string,
    memberId:    r.member_id as string,
    memberName:  r.member_name as string,
    date:        r.date as string,
    checkIn:     r.check_in as string | null,
    checkOut:    r.check_out as string | null,
    status:      (r.status as AttendanceRecord['status']) || 'present',
    hoursWorked: (r.hours_worked as number) || 0,
    notes:       r.notes as string | undefined,
  }
}

export async function getAttendance(): Promise<AttendanceRecord[]> {
  const { data, error } = await supabase.from('attendance').select('*').order('date', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToAttendance)
}

export async function upsertAttendance(a: Omit<AttendanceRecord, 'id'>): Promise<AttendanceRecord> {
  const { data, error } = await supabase.from('attendance').upsert({
    member_id: a.memberId, member_name: a.memberName, date: a.date,
    check_in: a.checkIn, check_out: a.checkOut, status: a.status,
    hours_worked: a.hoursWorked, notes: a.notes,
  }, { onConflict: 'member_id,date' }).select().single()
  if (error) throw error
  return rowToAttendance(data)
}

export async function updateAttendance(id: string, a: Partial<AttendanceRecord>): Promise<void> {
  const updates: Record<string, unknown> = {}
  if (a.checkIn     !== undefined) updates.check_in     = a.checkIn
  if (a.checkOut    !== undefined) updates.check_out    = a.checkOut
  if (a.status      !== undefined) updates.status       = a.status
  if (a.hoursWorked !== undefined) updates.hours_worked = a.hoursWorked
  if (a.notes       !== undefined) updates.notes        = a.notes
  const { error } = await supabase.from('attendance').update(updates).eq('id', id)
  if (error) throw error
}

export async function getLeaveBalances(): Promise<LeaveBalance[]> {
  const { data, error } = await supabase.from('leave_balances').select('*')
  if (error) throw error
  return (data ?? []).map(r => ({
    memberId:   r.member_id as string,
    memberName: r.member_name as string,
    casual: { total: r.casual_total as number, used: r.casual_used as number },
    sick:   { total: r.sick_total as number,   used: r.sick_used as number   },
    earned: { total: r.earned_total as number, used: r.earned_used as number },
  }))
}

export async function upsertLeaveBalance(lb: LeaveBalance): Promise<void> {
  const { error } = await supabase.from('leave_balances').upsert({
    member_id: lb.memberId, member_name: lb.memberName,
    casual_total: lb.casual.total, casual_used: lb.casual.used,
    sick_total: lb.sick.total, sick_used: lb.sick.used,
    earned_total: lb.earned.total, earned_used: lb.earned.used,
  }, { onConflict: 'member_id' })
  if (error) throw error
}
